import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { FONT_MAIN, glassCard } from "./MobileFamiliares";
import { ArrowLeft, CameraOff, Loader2, ScanFace, ShieldCheck } from "lucide-react";

const MOTIVOS = {
  nenhum_rosto: "Nenhum rosto detectado. Centralize o rosto no oval e tente de novo.",
  multiplos_rostos: "Mais de um rosto na imagem. Fotografe apenas você.",
  rosto_pequeno: "Rosto muito distante. Aproxime o celular.",
  imagem_desfocada: "Foto desfocada. Segure firme e tente novamente.",
  imagem_invalida: "Não foi possível ler a foto. Tente novamente.",
  rosto_nao_reconhecido: "Rosto não reconhecido. Tente novamente com boa iluminação.",
  muitas_tentativas: "Muitas tentativas. Aguarde 1 minuto e tente de novo.",
  sem_cadastro: "Cadastro facial não encontrado. Faça o cadastro primeiro.",
  storage_indisponivel: "Falha ao salvar a foto. Tente novamente.",
  cadastro_nao_encontrado: "Cadastro não encontrado. Faça login novamente.",
  "2fa_desabilitado": "Reconhecimento facial não está habilitado neste condomínio.",
};

export default function MobileFacial({ origem, eql, cpf, modo, onSucesso, onBloqueado, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOk, setCameraOk] = useState(false);
  const [erroCamera, setErroCamera] = useState(false);
  const [consentimento, setConsentimento] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [autoStatus, setAutoStatus] = useState("");

  const parar = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (!ativo) { s.getTracks().forEach((tr) => tr.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play().catch(() => {});
        }
        setCameraOk(true);
      } catch {
        if (ativo) setErroCamera(true);
      }
    })();
    return () => { ativo = false; parar(); };
  }, [parar]);

  const capturar = async (silencioso = false) => {
    if (enviando) return;
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const esc = Math.min(1, 720 / v.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(v.videoWidth * esc);
    canvas.height = Math.round(v.videoHeight * esc);
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    const b64 = canvas.toDataURL("image/jpeg", 0.85);
    setEnviando(true);
    try {
      const r = await fetch(`${API}/public/facial/${modo}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf, foto_b64: b64 }),
      });
      const d = await r.json();
      if (d.ok) {
        setEnviando(false);
        parar();
        if (modo === "enroll") toast.success("Rosto cadastrado! Agora confirme sua identidade.");
        onSucesso(d.token || null);
        return;
      }
      if (d.motivo === "muitas_tentativas") {
        setEnviando(false);
        parar();
        onBloqueado?.();
        return;
      }
      if (silencioso) {
        if (d.motivo === "rosto_nao_reconhecido") setAutoStatus("Rosto não reconhecido. Ajuste a posição e a luz.");
        else setAutoStatus("Procurando rosto... centralize no oval.");
      } else {
        toast.error(MOTIVOS[d.motivo] || "Não foi possível validar a foto. Tente novamente.");
      }
    } catch {
      if (!silencioso) toast.error("Falha de conexão. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  };

  const capturarRef = useRef(null);
  capturarRef.current = capturar;

  // Auto-captura no modo verify: basta olhar para a câmera; o botão fica como redundância.
  useEffect(() => {
    if (modo !== "verify" || !cameraOk || erroCamera) return undefined;
    setAutoStatus("Olhe para a câmera — reconhecimento automático ativo.");
    const iv = setInterval(() => capturarRef.current?.(true), 1800);
    return () => clearInterval(iv);
  }, [modo, cameraOk, erroCamera]);

  const titulo = modo === "enroll" ? "Cadastro Facial" : "Verificação Facial";

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-facial-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-facial-voltar" aria-label="Voltar"
          onClick={() => { parar(); onClose(); }}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }}
          data-testid="appmobile-facial-titulo">{titulo}</h1>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-10 flex flex-col items-center">
        <div className="w-full max-w-sm rounded-[14px] overflow-hidden relative" style={glassCard}
          data-testid="appmobile-facial-camera">
          <div className="w-full aspect-[3/4] relative" style={{ background: "#0a0b10" }}>
            <video ref={videoRef} playsInline muted autoPlay data-testid="appmobile-facial-video"
              className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center"
              data-testid="appmobile-facial-guia">
              <div className="w-48 h-64 rounded-[50%]"
                style={{ border: "3px solid rgba(138,180,255,0.9)", boxShadow: "0 0 0 9999px rgba(10,11,16,0.45)" }} />
            </div>
            {!cameraOk && !erroCamera && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                data-testid="appmobile-facial-iniciando">
                <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#8ab4ff" }} />
                <p className="text-xs" style={{ color: "#8d90a0" }}>Abrindo câmera...</p>
              </div>
            )}
            {erroCamera && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
                data-testid="appmobile-facial-erro-camera">
                <CameraOff className="h-8 w-8" style={{ color: "#ffb4ab" }} />
                <p className="text-xs" style={{ color: "#c3c6d7" }}>
                  Não foi possível acessar a câmera. Libere a permissão no navegador/aplicativo e tente novamente.
                </p>
              </div>
            )}
            {enviando && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                style={{ background: "rgba(10,11,16,0.7)" }} data-testid="appmobile-facial-enviando">
                <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#8ab4ff" }} />
                <p className="text-xs" style={{ color: "#c3c6d7" }}>
                  {modo === "enroll" ? "Cadastrando..." : "Verificando..."}
                </p>
              </div>
            )}
          </div>
        </div>

        {modo === "verify" && cameraOk && !erroCamera && (
          <p data-testid="appmobile-facial-auto-status"
            className="mt-3 text-xs flex items-center gap-1.5" style={{ color: "#8ab4ff" }}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {autoStatus || "Procurando rosto..."}
          </p>
        )}

        <div className="w-full max-w-sm mt-4 rounded-xl p-4 relative overflow-hidden" style={glassCard}
          data-testid="appmobile-facial-instrucoes">
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"
            style={{ color: "#b4c5ff" }}>
            <ScanFace className="h-4 w-4" /> Para um bom resultado
          </p>
          <ul className="text-xs flex flex-col gap-1.5" style={{ color: "#c3c6d7" }}>
            <li>Encoste-se em uma <b style={{ color: "#e1e2ed" }}>parede de fundo branco ou liso</b>.</li>
            <li>Fique em local bem iluminado, com a luz à sua frente.</li>
            <li>Centralize o rosto no oval, com expressão séria e neutra.</li>
            <li>Segure o celular firme na altura dos olhos.</li>
          </ul>
        </div>

        {modo === "enroll" && (
          <label className="w-full max-w-sm mt-3 flex items-start gap-3 cursor-pointer"
            data-testid="appmobile-facial-consentimento-label">
            <input type="checkbox" checked={consentimento} data-testid="appmobile-facial-consentimento"
              onChange={(e) => setConsentimento(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-blue-600 shrink-0" />
            <span className="text-[11px] leading-snug" style={{ color: "#8d90a0" }}>
              Autorizo o uso da minha imagem facial exclusivamente para liberar meu acesso ao condomínio
              (QR Acesso). Posso apagar este cadastro a qualquer momento em Meu Perfil.
            </span>
          </label>
        )}

        <button type="button" data-testid="appmobile-facial-capturar" onClick={() => capturar(false)}
          disabled={!cameraOk || enviando || (modo === "enroll" && !consentimento)}
          className="w-full max-w-sm mt-4 h-12 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-40"
          style={{ background: "#2563EB", boxShadow: "0 0 12px rgba(37,99,235,0.3)" }}>
          {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          {modo === "enroll" ? "Fotografar e cadastrar" : "Fotografar e verificar"}
        </button>
      </main>
    </div>
  );
}
